import {
    Handler,
    SharedGestureState,
    WebKitGestureEvent,
    createUseGesture,
    dragAction,
    pinchAction
} from '@use-gesture/react';
import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { SeatType } from '@cineamo/api/models/seat/SeatDto.types';

import { Point2D } from '../helpers/math';
import { isTouchScreen } from '../helpers/screen-type';
import { tailwindColors } from '../helpers/tailwind-config-helper';
import { classifyWheel, wheelZoomFactor } from '../helpers/wheel';
import { useResize } from '../hooks/useResize';
import { IconBeanbag16 } from '../icons/ic-beanbag';
import { IconBed16 } from '../icons/ic-bed';
import { IconCircleSolid16 } from '../icons/ic-circleSolid';
import { IconClose16 } from '../icons/ic-close';
import { IconHeartSolid16 } from '../icons/ic-heartSolid';
import { IconLogout16 } from '../icons/ic-logout';
import { IconWheelchair16 } from '../icons/ic-wheelchair';

import { Camera, CameraState } from './Camera';
import { Drawer, usableScreenObject, DrawerOptions } from './Drawer';
import SeatTooltip from './SeatTooltip';
import { CinemaRoom, RoomObject, Seat } from './typedefs';

export type SeatMapData = {
    room: CinemaRoom;
    seats: Seat[];
    roomObjects?: RoomObject[];
};

export type SeatMapApi = {
    redraw(): void;
};

const useGesture = createUseGesture([dragAction, pinchAction]);

/** Room below the back wall, in plan millimeters, for what stands on it. */
const BACK_WALL_MARGIN_MM = 600;
const DOUBLE_TAP_THRESHOLD = 300; // ms
const DOUBLE_TAP_DISTANCE = 30; // px

const iconComponents: Record<string, React.JSX.Element> = {
    exit: <IconLogout16 fill={tailwindColors.white.DEFAULT} />,
    dot: <IconCircleSolid16 fill={tailwindColors.darkBlue.DEFAULT} />,
    close: <IconClose16 fill={tailwindColors.white.DEFAULT} />,
    heart_dark: (
        <IconHeartSolid16
            fill={tailwindColors.black.DEFAULT}
            opacity={0.5}
        />
    ),
    wheelchair_dark: (
        <IconWheelchair16
            fill={tailwindColors.black.DEFAULT}
            opacity={0.5}
        />
    ),
    heart_light: (
        <IconHeartSolid16
            fill={tailwindColors.white.DEFAULT}
            opacity={0.5}
        />
    ),
    wheelchair_light: (
        <IconWheelchair16
            fill={tailwindColors.white.DEFAULT}
            opacity={0.5}
        />
    ),
    bed_dark: (
        <IconBed16
            fill={tailwindColors.black.DEFAULT}
            opacity={0.5}
        />
    ),
    bed_light: (
        <IconBed16
            fill={tailwindColors.white.DEFAULT}
            opacity={0.5}
        />
    ),
    beanbag_dark: (
        <IconBeanbag16
            fill={tailwindColors.black.DEFAULT}
            opacity={0.5}
        />
    ),
    beanbag_light: (
        <IconBeanbag16
            fill={tailwindColors.white.DEFAULT}
            opacity={0.5}
        />
    )
};

function renderSvgToImage(icon: React.JSX.Element) {
    const markup = renderToStaticMarkup(icon);
    const encoded = encodeURIComponent(markup);
    const dataUri = `data:image/svg+xml;charset=utf-8,${encoded}`;
    const img = new Image();
    img.src = dataUri;
    img.crossOrigin = 'anonymous';
    return { img, dataUri };
}

type DefaultSeatMapProps = {
    data: SeatMapData;
    seatColorsByCategory: DrawerOptions['seatColorsByCategory'];
    className?: string;
};

type SeatMapProps = DefaultSeatMapProps &
    (
        | {
              isDisabled: true;
              onSeatClick?: never;
              onMouseEnter?: never;
              selectedSeatColor?: never;
              drawVersion?: never;
              trackEvent?: never;
          }
        | {
              isDisabled: false | undefined;
              onSeatClick: (seat: Seat) => void;
              onMouseEnter: () => void;
              selectedSeatColor: DrawerOptions['selectedSeatColor'];
              drawVersion: number;
              trackEvent: (event: string) => void;
          }
    );

const SeatMap: React.FC<SeatMapProps> = ({
    data,
    onSeatClick,
    onMouseEnter,
    seatColorsByCategory,
    selectedSeatColor,
    drawVersion,
    isDisabled,
    trackEvent,
    className
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasElRef = useRef<HTMLCanvasElement>(null);
    const drawerRef = useRef<Drawer>();
    const cameraRef = useRef<Camera>();
    const pixelRatio = useRef(1);
    const iconsRef = useRef<Record<string, HTMLImageElement>>({});
    const lastTapRef = useRef<{ time: number; x: number; y: number } | null>(null);
    const pinchStartRef = useRef<{ camera: CameraState; focal: Point2D } | null>(null);
    const drawRequestRef = useRef(0);
    const dataRef = useRef(data);
    useEffect(() => {
        dataRef.current = data;
    });
    const t = useTranslations();

    const { width: containerWidth, height: containerHeight } = useResize(containerRef, 200);

    // Fill the container. On mobile (no container height), fall back to room aspect ratio. The room
    // can be missing while its query is still pending, so this must not assume one.
    const width = containerWidth;
    const height =
        containerHeight > 0 ? containerHeight : data.room ? (data.room.depth * containerWidth) / data.room.width : 0;

    const [cursor, setCursor] = useState('default');
    const [tooltip, setTooltip] = useState<{ message: string; position: { x: number; y: number } } | null>(null);

    const hasSelectedSeats = useMemo(() => {
        return data.seats?.some((seat) => seat.isSelected);
    }, [data.seats]);

    // Trigger redraw when selection state changes
    useEffect(() => {
        const drawer = drawerRef.current;
        const camera = cameraRef.current;
        if (drawer && camera) {
            drawer.drawFrame(dataRef.current, camera.state);
        }
    }, [hasSelectedSeats, data.seats?.length, data.room, drawVersion]);

    // Initialize icons and drawer
    useEffect(() => {
        pixelRatio.current = window.devicePixelRatio;

        const ctx = canvasElRef.current?.getContext('2d');
        async function prepareImages() {
            if (Object.keys(iconsRef.current).length > 0) {
                return;
            }
            await Promise.all(
                Object.entries(iconComponents).map(([key, icon]) => {
                    const { img } = renderSvgToImage(icon);
                    iconsRef.current[key] = img;
                    return new Promise((resolve) => {
                        img.onload = () => resolve([key, img]);
                    });
                })
            );
        }
        if (ctx) {
            prepareImages().then(() => {
                drawerRef.current = new Drawer(ctx, {
                    seatColorsByCategory,
                    selectedSeatColor,
                    screenLabel: t('tickets.screen'),
                    icons: iconsRef.current
                });
            });
        }
    }, [seatColorsByCategory, selectedSeatColor, t, pixelRatio]);

    // Initialize/update camera when dimensions change
    useEffect(() => {
        const drawer = drawerRef.current;
        // The room is guarded on its own rather than through `height`: the fallback above yields 0
        // without a room, but a container that has its own height keeps height non-zero, and the
        // room measurements below would then be read off nothing.
        if (!drawer || !width || !height || !data.room) return;

        const canvasW = width * pixelRatio.current;
        const canvasH = height * pixelRatio.current;

        // Compute actual room content height (seats + screen area)
        const roomScale = canvasW / data.room.width;
        const screenObject = usableScreenObject(data.roomObjects ?? [], data.room);
        const screenDepth = screenObject ? screenObject.depth * roomScale : canvasH * 0.06 + 30;
        // The margin keeps signs on the back wall (exits) whole instead of cut by the map's lower edge.
        const contentH = (data.room.depth + BACK_WALL_MARGIN_MM) * roomScale + screenDepth;

        if (cameraRef.current) {
            cameraRef.current.updateViewport(canvasW, canvasH, canvasW, contentH);
            drawer.drawFrame(data, cameraRef.current.state);
        } else {
            const camera = new Camera({
                viewportWidth: canvasW,
                viewportHeight: canvasH,
                contentWidth: canvasW,
                contentHeight: contentH,
                roomWidthMm: data.room.width,
                onUpdate: () => {
                    drawer.drawFrame(dataRef.current, camera.state);
                }
            });
            cameraRef.current = camera;
            // Initial draw
            drawer.drawFrame(data, camera.state);
        }
    }, [data, width, height]);

    // Cleanup
    useEffect(() => {
        return () => {
            cameraRef.current?.destroy();
            cancelAnimationFrame(drawRequestRef.current);
        };
    }, []);

    // Pointer and wheel events can arrive faster than the display refreshes; draw at most once per frame.
    const requestDraw = useCallback(() => {
        if (drawRequestRef.current) return;
        drawRequestRef.current = requestAnimationFrame(() => {
            drawRequestRef.current = 0;
            const drawer = drawerRef.current;
            const camera = cameraRef.current;
            if (drawer && camera) drawer.drawFrame(dataRef.current, camera.state);
        });
    }, []);

    const onMouseMove = useCallback(
        ({ event: { offsetX, offsetY } }: SharedGestureState & { event: MouseEvent }) => {
            if (isTouchScreen) return;

            const drawer = drawerRef.current;
            const camera = cameraRef.current;
            if (!drawer || !camera) return;

            const x = offsetX * pixelRatio.current;
            const y = offsetY * pixelRatio.current;
            const worldPoint = camera.screenToWorld({ x, y });
            const hoveredSeat = drawer.findSeatByPoint(worldPoint);

            let newCursor = 'default';
            if (hoveredSeat) {
                const isNonSelectable =
                    hoveredSeat.isLocked || [SeatType.WHEELCHAIR, SeatType.DISABLED].includes(hoveredSeat.type);
                newCursor = isNonSelectable ? 'not-allowed' : 'pointer';

                // Show tooltip for non-selectable seats (only in interactive mode)
                if (isNonSelectable && !isDisabled) {
                    const isWheelchair = [SeatType.WHEELCHAIR, SeatType.DISABLED].includes(hoveredSeat.type);
                    const screenPos = camera.worldToScreen(worldPoint);
                    setTooltip({
                        message: isWheelchair ? t('tickets.seat-wheelchair-accessible') : t('tickets.seat-unavailable'),
                        position: {
                            x: screenPos.x / pixelRatio.current,
                            y: screenPos.y / pixelRatio.current
                        }
                    });
                } else {
                    setTooltip(null);
                }
            } else {
                setTooltip(null);
            }
            if (isDisabled) {
                newCursor = 'not-allowed';
            }
            setCursor(newCursor);
        },
        [isDisabled]
    );

    const handleSeatClick = useCallback(
        (screenX: number, screenY: number) => {
            if (isDisabled) return;

            const drawer = drawerRef.current;
            const camera = cameraRef.current;
            if (!drawer || !camera) return;

            const worldPoint = camera.screenToWorld({ x: screenX, y: screenY });
            const clickedSeat = drawer.findSeatByPoint(worldPoint);

            if (clickedSeat) {
                const isWheelchairOrDisabled = [SeatType.WHEELCHAIR, SeatType.DISABLED].includes(clickedSeat.type);

                if (isWheelchairOrDisabled || clickedSeat.isLocked) {
                    // Show tooltip for non-selectable seats on tap (touch screens)
                    const screenPos = camera.worldToScreen(worldPoint);
                    setTooltip({
                        message: isWheelchairOrDisabled
                            ? t('tickets.seat-wheelchair-accessible')
                            : t('tickets.seat-unavailable'),
                        position: {
                            x: screenPos.x / pixelRatio.current,
                            y: screenPos.y / pixelRatio.current
                        }
                    });
                    return;
                }

                if (!clickedSeat.isProcessing) {
                    clickedSeat.isSelected = !clickedSeat.isSelected;
                    drawer.drawFrame(dataRef.current, camera.state);
                    onSeatClick(clickedSeat);
                }
            } else {
                setTooltip(null);
            }
        },
        [onSeatClick, isDisabled]
    );

    const onClick = useCallback(
        ({ dragging, event: { offsetX, offsetY } }: SharedGestureState & { event: MouseEvent }) => {
            if (dragging) return;

            const x = offsetX * pixelRatio.current;
            const y = offsetY * pixelRatio.current;
            const camera = cameraRef.current;
            const isOnSeat = !!camera && !!drawerRef.current?.findSeatByPoint(camera.screenToWorld({ x, y }));

            // A tap on a seat takes it at once; a double tap on the floor zooms, as in the studio plan.
            if (isOnSeat) {
                lastTapRef.current = null;
                handleSeatClick(x, y);
                return;
            }

            const now = Date.now();
            const lastTap = lastTapRef.current;
            if (
                lastTap &&
                now - lastTap.time < DOUBLE_TAP_THRESHOLD &&
                Math.hypot(x - lastTap.x, y - lastTap.y) < DOUBLE_TAP_DISTANCE * pixelRatio.current
            ) {
                lastTapRef.current = null;
                camera?.doubleTap({ x, y });
                trackEvent?.('Double Tap');
                return;
            }

            lastTapRef.current = { time: now, x, y };
            handleSeatClick(x, y);
        },
        [handleSeatClick, trackEvent]
    );

    const onDrag = useCallback<Handler<'drag', PointerEvent | MouseEvent | TouchEvent | KeyboardEvent>>(
        ({ pinching, cancel, first, last, delta: [x, y], velocity: [vx, vy], direction: [dx, dy] }) => {
            if (pinching) return cancel();

            const camera = cameraRef.current;
            if (!camera) return;

            if (first) {
                camera.beginGesture();
            }

            if (x !== 0 || y !== 0) {
                trackEvent?.('Drag');
            }

            const scaledDx = x * pixelRatio.current;
            const scaledDy = y * pixelRatio.current;
            camera.updatePan(scaledDx, scaledDy);
            requestDraw();

            if (last) {
                const momentumScale = 800;
                camera.endPan(
                    vx * dx * momentumScale * pixelRatio.current,
                    vy * dy * momentumScale * pixelRatio.current
                );
            }
        },
        [trackEvent, requestDraw]
    );

    const onPinch = useCallback<Handler<'pinch', PointerEvent | TouchEvent | WheelEvent | WebKitGestureEvent>>(
        ({ event, first, last, movement: [ratio], origin: [clientX, clientY] }) => {
            // Trackpad pinch arrives as ctrl+wheel and is handled by the wheel listener below.
            if (event.type === 'wheel') return;

            const camera = cameraRef.current;
            const canvas = canvasElRef.current;
            if (!camera || !canvas) return;

            // `origin` is the fingers' midpoint in client coordinates.
            const rect = canvas.getBoundingClientRect();
            const focal: Point2D = {
                x: (clientX - rect.left) * pixelRatio.current,
                y: (clientY - rect.top) * pixelRatio.current
            };

            if (first || !pinchStartRef.current) {
                camera.beginGesture();
                pinchStartRef.current = { camera: camera.state, focal };
                trackEvent?.('Pinch');
            }

            const start = pinchStartRef.current;
            camera.pinchTo(start.camera, start.focal, ratio, focal);
            requestDraw();

            if (last) {
                pinchStartRef.current = null;
                camera.endScale();
            }
        },
        [trackEvent, requestDraw]
    );

    useGesture(
        {
            onMouseMove,
            onClick,
            onDrag,
            onPinch
        },
        {
            target: canvasElRef,
            drag: {
                filterTaps: true
            }
        }
    );

    // Wheel as in the studio plan: a mouse wheel or a trackpad pinch zooms at the pointer, a trackpad
    // two-finger scroll pans. A scroll that cannot move the map any further scrolls the page instead.
    useEffect(() => {
        const onWheel = (event: WheelEvent) => {
            const camera = cameraRef.current;
            if (!camera) return;

            const ratio = pixelRatio.current;
            const isZoom = classifyWheel(event) === 'zoom';
            const lineHeight = event.deltaMode === 1 ? 16 : 1;
            const hasMoved = isZoom
                ? camera.jumpBy(0, 0, wheelZoomFactor(event), {
                      x: event.offsetX * ratio,
                      y: event.offsetY * ratio
                  })
                : camera.jumpBy(-event.deltaX * lineHeight * ratio, -event.deltaY * lineHeight * ratio);
            if (!hasMoved && !isZoom) return;

            event.preventDefault();
            trackEvent?.(isZoom ? 'Mouse Wheel' : 'Wheel Pan');
            requestDraw();
        };

        const canvasEl = canvasElRef.current;
        canvasEl?.addEventListener('wheel', onWheel, { passive: false });
        return () => canvasEl?.removeEventListener('wheel', onWheel);
    }, [trackEvent, requestDraw]);

    return (
        <div
            ref={containerRef}
            className={classnames('relative', className)}>
            <canvas
                onMouseEnter={onMouseEnter}
                className={classnames('block touch-none select-none mx-auto', `cursor-${cursor}`)}
                style={{ width, height }}
                // eslint-disable-next-line react-hooks/refs -- intentional: device pixel ratio is captured once after mount and used to size the canvas backing store
                width={width * pixelRatio.current}
                // eslint-disable-next-line react-hooks/refs -- intentional: device pixel ratio is captured once after mount and used to size the canvas backing store
                height={height * pixelRatio.current}
                ref={canvasElRef}
            />
            <SeatTooltip
                message={tooltip?.message ?? null}
                position={tooltip?.position ?? { x: 0, y: 0 }}
            />
        </div>
    );
};

export default SeatMap;
